import LoadingSpinner from "@/components/LoadingSpinner";
import useRules from "@/hooks/useRules";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import useApi from "@/hooks/useApi";
import * as React from "react";
import RuleRowItem from "./RuleRowItem";
import { useEffect, useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import RuleEditModal from "./RuleEditModal";
import QuickRuleSetupModal from "./QuickRuleSetupModal";
import RuleVersionHistoryModal from "./RuleVersionHistoryModal";
import { Rule } from "./types";
import IconChevronDown from "@/icons/IconChevronDown";
import IconChevronUp from "@/icons/IconChevronUp";

// ── Conflict Detection ─────────────────────────────────────────────────────────

interface TeamRoutingDto {
  id: number;
  name: string;
  isDeleted: boolean;
  autoResolve: boolean;
  autoResolveTime: number; // stored in seconds in DB, C# uses as minutes — see WebJob
  queueTimeoutEnabled: boolean;
  queueTimeoutMinutes: number;
}

interface ConflictEntry {
  ruleIndex: number;
  rulePriority: number;
  ruleMinutes: number;
  teamId: number;
  teamName: string;
  teamMinutes: number;
  ruleWins: boolean; // true = rule's shorter timer fires first
}

function useConflicts(rules: Rule[]) {
  const api = useApi();
  const [teams, setTeams] = React.useState<TeamRoutingDto[]>([]);

  React.useEffect(() => {
    api.messaging.get("Teams/List")
      .then(r => r.ok ? r.json() : [])
      .then(data => setTeams(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [api]);

  return React.useMemo<ConflictEntry[]>(() => {
    const autoResolveRules = rules
      .map((r, i) => ({ rule: r, index: i, minutes: parseInt((r.value as { minutes?: string })?.minutes ?? "", 10) }))
      .filter(x => x.rule.trigger === "resolve-chat" && !isNaN(x.minutes) && x.minutes > 0);

    const activeTeams = teams.filter(t => !t.isDeleted && t.autoResolve && t.autoResolveTime > 0);

    const conflicts: ConflictEntry[] = [];
    for (const { rule, index, minutes: ruleMinutes } of autoResolveRules) {
      for (const team of activeTeams) {
        if (ruleMinutes !== team.autoResolveTime) {
          conflicts.push({
            ruleIndex: index,
            rulePriority: rule.priority,
            ruleMinutes,
            teamId: team.id,
            teamName: team.name,
            teamMinutes: team.autoResolveTime,
            ruleWins: ruleMinutes < team.autoResolveTime,
          });
        }
      }
    }
    return conflicts;
  }, [rules, teams]);
}

function ConflictPanel({ rules, onEditRule }: { rules: Rule[]; onEditRule: (index: number) => void }) {
  const conflicts = useConflicts(rules);
  const [open, setOpen] = React.useState(true);

  if (conflicts.length === 0) return null;

  return (
    <div className="mb-4 border border-amber-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-sm font-semibold text-amber-800 text-left transition-colors"
      >
        <span className="flex items-center gap-2">
          <i className="fa fa-exclamation-triangle text-amber-500 text-xs" aria-hidden="true" />
          {conflicts.length} competing auto-resolve setting{conflicts.length !== 1 ? "s" : ""} detected
        </span>
        {open
          ? <IconChevronUp width={14} height={14} className="text-amber-400 shrink-0" />
          : <IconChevronDown width={14} height={14} className="text-amber-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white border-t border-amber-100 space-y-3">
          <p className="text-xs text-slate-600">
            Each row below shows a rule and a team setting that both control auto-resolve.
            <strong> The shorter timer always wins</strong> — the longer one will never fire for that team&apos;s conversations.
            Fix the conflict by aligning the timers or removing one of the settings.
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 border-b border-slate-100">
                <th className="text-left py-1.5 pr-3 font-medium">Rule</th>
                <th className="text-left py-1.5 pr-3 font-medium">Team</th>
                <th className="text-center py-1.5 pr-3 font-medium">Rule timer</th>
                <th className="text-center py-1.5 pr-3 font-medium">Team timer</th>
                <th className="text-left py-1.5 font-medium">What actually fires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {conflicts.map((c, i) => (
                <tr key={i} className="align-middle">
                  <td className="py-1.5 pr-3">
                    <button
                      type="button"
                      onClick={() => onEditRule(c.ruleIndex)}
                      className="text-cornflower-blue-600 hover:underline font-medium"
                    >
                      Rule #{c.rulePriority}
                    </button>
                  </td>
                  <td className="py-1.5 pr-3">
                    <a href="/messaging/settings/teams-routing" className="text-cornflower-blue-600 hover:underline">
                      {c.teamName}
                    </a>
                  </td>
                  <td className={`py-1.5 pr-3 text-center font-mono ${c.ruleWins ? "text-success-green-700 font-semibold" : "text-slate-400 line-through"}`}>
                    {c.ruleMinutes} min
                  </td>
                  <td className={`py-1.5 pr-3 text-center font-mono ${!c.ruleWins ? "text-success-green-700 font-semibold" : "text-slate-400 line-through"}`}>
                    {c.teamMinutes} min
                  </td>
                  <td className="py-1.5 text-slate-600">
                    {c.ruleWins
                      ? <span><span className="font-medium text-success-green-700">Rule #{c.rulePriority}</span> ({c.ruleMinutes} min) — team setting never triggers</span>
                      : <span><span className="font-medium text-success-green-700">Team &ldquo;{c.teamName}&rdquo;</span> ({c.teamMinutes} min) — this rule never triggers</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-400">
            To fix: either remove the dormant setting or update both timers to match.
            Go to <a href="/messaging/settings/teams-routing" className="text-cornflower-blue-500 hover:underline">Teams &amp; Routing</a> to adjust team settings.
          </p>
        </div>
      )}
    </div>
  );
}

const RULE_DRAFT_KEY = "rule_draft";

export default function RuleManager() {
  const { rules: workflowRules, loading, remove } = useRules();
  const { enableQuickStartWizards } = useAdminFeatures();

  const [workingRuleIndex, setWorkingRuleIndex] = useState<number | null | undefined>(undefined);
  const [manualPreset, setManualPreset] = useState<Partial<Rule> | undefined>(undefined);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [historyRule, setHistoryRule] = useState<Rule | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [resumableDraft, setResumableDraft] = useState<Partial<Rule> | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem(RULE_DRAFT_KEY);
    if (raw) {
      try {
        setResumableDraft(JSON.parse(raw));
      } catch {
        sessionStorage.removeItem(RULE_DRAFT_KEY);
      }
    }
  }, []);

  // Clear selection when rules change (e.g. after delete)
  useEffect(() => {
    setSelected(new Set());
  }, [workflowRules.length]);

  function resumeDraft() {
    if (!resumableDraft) return;
    sessionStorage.removeItem(RULE_DRAFT_KEY);
    setManualPreset(resumableDraft);
    setWorkingRuleIndex(null);
    setResumableDraft(null);
  }

  function discardDraft() {
    sessionStorage.removeItem(RULE_DRAFT_KEY);
    setResumableDraft(null);
  }

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selected.size === workflowRules.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(workflowRules.map((r) => r.id).filter((id): id is number => id !== undefined)));
    }
  }

  async function deleteSelected() {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!confirm(`Delete ${count} rule${count !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const toDelete = workflowRules.filter((r) => r.id !== undefined && selected.has(r.id));
      for (const rule of toDelete) {
        await remove(rule);
      }
      setSelected(new Set());
    } finally {
      setBulkDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  function renderModal() {
    if (showQuickSetup) {
      return (
        <QuickRuleSetupModal
          close={() => setShowQuickSetup(false)}
          openManualEditor={(preset) => {
            setShowQuickSetup(false);
            setManualPreset(preset);
            setWorkingRuleIndex(null);
          }}
        />
      );
    }

    if (workingRuleIndex !== undefined) {
      const rule =
        workingRuleIndex === null
          ? manualPreset
          : workflowRules[workingRuleIndex];
      return (
        <RuleEditModal
          rule={rule}
          close={() => {
            setWorkingRuleIndex(undefined);
            setManualPreset(undefined);
          }}
        />
      );
    }

    return null;
  }

  const allSelected = workflowRules.length > 0 && selected.size === workflowRules.length;
  const someSelected = selected.size > 0 && !allSelected;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Routing Rules</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Rules run in priority order. The first matching rule per trigger type is applied.
            {workflowRules.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                {workflowRules.length} rule{workflowRules.length !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          {enableQuickStartWizards && (
            <SecondaryBlueButton
              onClick={() => setShowQuickSetup(true)}
              label="⚡ Quick Setup"
            />
          )}
          <PrimaryBlueButton
            onClick={() => {
              setManualPreset(undefined);
              setWorkingRuleIndex(null);
            }}
            label="+ New Rule"
          />
        </div>
      </div>

      {/* Resume draft banner */}
      {resumableDraft && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="text-cornflower-blue-600 shrink-0">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <p className="text-sm text-cornflower-blue-800 flex-1">
            You left while building a rule. Want to pick up where you left off?
          </p>
          <button type="button" onClick={resumeDraft} className="shrink-0 text-sm font-medium text-cornflower-blue-700 hover:text-cornflower-blue-900 transition-colors">
            Continue
          </button>
          <button type="button" onClick={discardDraft} className="shrink-0 text-sm text-slate-400 hover:text-slate-600 transition-colors">
            Discard
          </button>
        </div>
      )}

      {/* Conflict detection panel — shown when rules and team settings compete */}
      <ConflictPanel
        rules={workflowRules}
        onEditRule={(index) => { setManualPreset(undefined); setWorkingRuleIndex(index); }}
      />

      {/* How Rules Work collapsible */}
      <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-600 text-left transition-colors"
        >
          <span className="flex items-center gap-2">
            <span className="text-slate-400">ℹ</span>
            How do routing rules work?
          </span>
          {showHowItWorks
            ? <IconChevronUp width={14} height={14} className="text-slate-400" />
            : <IconChevronDown width={14} height={14} className="text-slate-400" />}
        </button>
        {showHowItWorks && (
          <div className="px-4 py-3 text-sm text-slate-600 bg-white border-t border-slate-200 space-y-2">
            <p><strong>Priority order:</strong> Rules run from priority 1 upward. The first matching rule for each trigger type wins — later rules are skipped for that trigger.</p>
            <p><strong>No conditions = always matches.</strong> A rule with no conditions acts as a catch-all fallback. Put it last so more specific rules run first.</p>
            <p><strong>WHEN vs ALWAYS:</strong> Rules with conditions show "WHEN [conditions]". Rules without conditions show "ALWAYS" — they always apply.</p>
            <p>Use <strong>⚡ Quick Setup</strong> for a guided wizard, or <strong>+ New Rule</strong> to build manually.</p>
          </div>
        )}
      </div>

      {/* Rules table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">

        {/* Bulk action bar — shown when items are selected */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-cornflower-blue-50 border-b border-cornflower-blue-200">
            <span className="text-sm font-medium text-cornflower-blue-700">
              {selected.size} rule{selected.size !== 1 ? "s" : ""} selected
            </span>
            <button
              onClick={deleteSelected}
              disabled={bulkDeleting}
              className="text-sm text-red-600 hover:text-red-800 font-medium border border-red-200 hover:border-red-400 px-3 py-1 rounded-lg bg-white transition-colors disabled:opacity-40"
            >
              {bulkDeleting ? "Deleting…" : "Delete selected"}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors ml-1"
            >
              Clear
            </button>
          </div>
        )}

        {/* Column headers */}
        <div className="grid grid-cols-[2rem_2.5rem_14rem_1fr_auto] gap-x-4 items-center px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-cornflower-blue-500 cursor-pointer"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleSelectAll}
              aria-label="Select all rules"
              disabled={workflowRules.length === 0}
            />
          </div>
          <div className="text-center">#</div>
          <div>Trigger</div>
          <div>Condition / Target</div>
          <div className="pr-1">Actions</div>
        </div>

        {/* Rule rows */}
        {workflowRules.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-slate-300 mb-3">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p className="text-slate-500 text-sm font-medium">No routing rules yet</p>
            <p className="text-slate-400 text-xs mt-1 mb-4">Rules control which team, bot, or survey a conversation goes to.</p>
            <button
              onClick={() => { setManualPreset(undefined); setWorkingRuleIndex(null); }}
              className="text-sm text-cornflower-blue-600 hover:text-cornflower-blue-800 font-medium border border-cornflower-blue-200 hover:border-cornflower-blue-400 px-4 py-1.5 rounded-lg transition-colors"
            >
              + Create your first rule
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {workflowRules.map((r, i) => (
              <RuleRowItem
                key={r.id ?? i}
                rule={r}
                selected={r.id !== undefined && selected.has(r.id)}
                onToggleSelect={() => r.id !== undefined && toggleSelect(r.id)}
                editRule={() => {
                  setManualPreset(undefined);
                  setWorkingRuleIndex(i);
                }}
                showHistory={() => setHistoryRule(r)}
              />
            ))}
          </div>
        )}
      </div>

      {renderModal()}
      {historyRule && (
        <RuleVersionHistoryModal
          rule={historyRule}
          close={() => setHistoryRule(null)}
          onRestore={(restored) => {
            setHistoryRule(null);
            setManualPreset(restored);
            setWorkingRuleIndex(null);
          }}
        />
      )}
    </div>
  );
}
